import supabase from '../supabase/supabase'

/**
 * Invitation Service
 * Handles user invitation operations
 */

/**
 * Get current user's tenant ID
 */
const getCurrentTenantId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('No authenticated user')
  }

  const { data, error } = await supabase
    .from('tenant_members')
    .select('tenant_id, role')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  
  if (data.role !== 'admin') {
    throw new Error('Only admins can send invitations')
  }

  return data.tenant_id
}

/**
 * Send invitation to user
 */
export const sendInvitation = async (email, role = 'user') => {
  try {
    const tenantId = await getCurrentTenantId()
    const { data: { user } } = await supabase.auth.getUser()

    // Check if user already exists in tenant
    const { data: existingMember } = await supabase
      .from('tenant_members')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('user_id', (
        await supabase
          .from('auth_users')
          .select('id')
          .eq('email', email)
          .single()
      )?.data?.id)
      .single()

    if (existingMember) {
      throw new Error('User is already a member of this organization')
    }

    // Check if invitation already exists
    const { data: existingInvitation } = await supabase
      .from('invitations')
      .select('id, expires_at')
      .eq('tenant_id', tenantId)
      .eq('email', email)
      .is('accepted_at', null)
      .single()

    if (existingInvitation) {
      if (new Date(existingInvitation.expires_at) > new Date()) {
        throw new Error('An active invitation already exists for this email')
      } else {
        // Delete expired invitation
        await supabase
          .from('invitations')
          .delete()
          .eq('id', existingInvitation.id)
      }
    }

    // Generate secure token
    const { data: tokenData } = await supabase.rpc('generate_invitation_token')
    const token = tokenData || `inv_${Date.now()}_${Math.random().toString(36).substring(7)}`

    // Create invitation
    const { data, error } = await supabase
      .from('invitations')
      .insert([
        {
          tenant_id: tenantId,
          email: email.toLowerCase().trim(),
          role: role,
          invited_by: user.id,
          token: token,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        }
      ])
      .select(`
        *,
        tenants(name)
      `)
      .single()

    if (error) throw error

    // TODO: Send invitation email via Supabase Edge Function or external service
    // For now, return the invitation with token for manual sharing
    return {
      ...data,
      invitationUrl: `${window.location.origin}/#/accept-invitation?token=${token}`
    }
  } catch (error) {
    console.error('Error sending invitation:', error)
    throw error
  }
}

/**
 * Get all invitations for current tenant
 */
export const getInvitations = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('tenant_id', tenantId)
      .is('accepted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching invitations:', error)
    throw error
  }
}

/**
 * Get invitation by token
 */
export const getInvitationByToken = async (token) => {
  try {
    console.log('🔍 Looking for invitation with token:', token)
    
    const { data, error } = await supabase
      .from('invitations')
      .select(`
        *,
        tenants(id, name, slug)
      `)
      .eq('token', token)
      .is('accepted_at', null)
      .maybeSingle()

    console.log('📊 Query result:', { data, error })

    if (error) {
      console.error('❌ Database error:', error)
      throw error
    }
    
    if (!data) {
      console.error('❌ No invitation found for token:', token)
      throw new Error('Invitation not found')
    }

    console.log('✅ Found invitation:', data)

    // Check if expired
    if (new Date(data.expires_at) < new Date()) {
      throw new Error('This invitation has expired')
    }

    return data
  } catch (error) {
    console.error('Error fetching invitation:', error)
    throw error
  }
}

/**
 * Accept invitation and join tenant
 */
export const acceptInvitation = async (token) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('You must be logged in to accept an invitation')
    }

    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/accept-invite',
        token
      }
    })

    if (error) {
      console.error('Edge function acceptInvitation error:', error)
      throw new Error(error.message || 'Failed to accept invitation')
    }

    return data
  } catch (error) {
    console.error('Error accepting invitation:', error)
    throw error
  }
}

/**
 * Cancel (delete) invitation
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const tenantId = await getCurrentTenantId()

    const { error } = await supabase
      .from('invitations')
      .delete()
      .eq('id', invitationId)
      .eq('tenant_id', tenantId)

    if (error) throw error

    return true
  } catch (error) {
    console.error('Error canceling invitation:', error)
    throw error
  }
}

/**
 * Resend invitation (creates new token)
 */
export const resendInvitation = async (invitationId) => {
  try {
    const tenantId = await getCurrentTenantId()

    // Get existing invitation
    const { data: invitation, error: fetchError } = await supabase
      .from('invitations')
      .select('email, role')
      .eq('id', invitationId)
      .eq('tenant_id', tenantId)
      .single()

    if (fetchError) throw fetchError

    // Delete old invitation
    await cancelInvitation(invitationId)

    // Create new invitation
    return await sendInvitation(invitation.email, invitation.role)
  } catch (error) {
    console.error('Error resending invitation:', error)
    throw error
  }
}

export default {
  sendInvitation,
  getInvitations,
  getInvitationByToken,
  acceptInvitation,
  cancelInvitation,
  resendInvitation
}